import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { SpreadType, Topic, WalletSummaryResponse } from '../lib/types';
import FixedLines from '../components/common/FixedLines';
import UiIcon from '../components/common/UiIcon';
import { SITE_CONFIG } from '../config/site';
import { getWalletSummary } from '../lib/api';
import { getAuthSession, startGoogleLogin, type AuthUser } from '../lib/auth';
import { trackEvent } from '../lib/analytics';
import {
  getSelectionRoute,
  getSpreadChuruCost,
  getSpreadLabel,
  persistReadingSelection,
  saveWalletRechargeIntent,
} from '../lib/walletFlow';

type QuestionIcon = 'sparkles' | 'compass' | 'heart' | 'moon' | 'coin' | 'eye';

interface QuestionPreset {
  id: string;
  topic: Topic;
  title: string;
  subtitle: string;
  readingAngleLines: string[];
  sessionBeatLines: string[];
  icon: QuestionIcon;
  bgGradient: string;
  borderColor: string;
  iconColor: string;
}

interface SessionGuideCard {
  eyebrow: string;
  lines: string[];
}

const THEME_INTRO_LINES: Record<SpreadType, string[]> = {
  'one-card': ['한 장으로 바로 짚기 좋은 질문을 모아두었습니다.', '지금 제일 먼저 떠오르는 것 하나만 골라 주세요.'],
  'three-card': ['흐름으로 읽기 좋은 질문을 골라두었습니다.', '시작점과 다음 장면까지 보고 싶은 고민을 골라 주세요.'],
  'celtic-cross': ['깊게 펼쳐볼 만한 질문을 모아두었습니다.', '가볍게 넘기기 어려운 고민이라면 여기서 골라 주세요.'],
};

function isSpreadType(value: string | null | undefined): value is SpreadType {
  return value === 'one-card' || value === 'three-card' || value === 'celtic-cross';
}

const SESSION_GUIDE_CARDS: SessionGuideCard[] = [
  {
    eyebrow: '질문 고르는 기준',
    lines: ['자꾸 다시 떠오르는 고민을 고르세요.', '말끔한 질문보다 솔직한 질문이 더 잘 읽힙니다.'],
  },
  {
    eyebrow: '선택 후 흐름',
    lines: ['질문을 고르면 카드 선택으로 넘어갑니다.', '결제가 끝나면 카드가 한 장씩 열립니다.'],
  },
  {
    eyebrow: '세션 톤',
    lines: ['카드 뜻만 늘어놓지 않습니다.', '왜 지금 이게 마음에 걸리는지부터 봅니다.'],
  },
];

const QUESTIONS_BY_SPREAD: Record<SpreadType, QuestionPreset[]> = {
  'one-card': [
    {
      id: 'one-1',
      topic: '연애',
      title: '지금 그 사람에게 먼저 연락해도 될까?',
      subtitle: '기다릴지, 오늘 한 번 움직여볼지 한 장으로 봅니다.',
      readingAngleLines: ['상대가 지금 연락을 받을 수 있는 상태인지,', '내가 먼저 움직이면 흐름이 어떻게 바뀌는지', '한 장으로 짚습니다.'],
      sessionBeatLines: ['망설임의 이유를 빠르게 짚고', '보낼지 멈출지 직답을 듣고 싶을 때'],
      icon: 'sparkles',
      bgGradient: 'from-[#2d1e18] to-[#1a1614]',
      borderColor: 'border-gold/30',
      iconColor: 'text-gold/80',
    },
    {
      id: 'one-2',
      topic: '직장',
      title: '지금 회사, 더 버틸까 그만둘까?',
      subtitle: '지친 마음과 현실적인 흐름을 따로 놓고 봅니다.',
      readingAngleLines: ['남아야 하는 이유와', '계속 나를 소진시키는 이유를', '지금 판에서 갈라 읽습니다.'],
      sessionBeatLines: ['감정과 현실을 나눠 보고', '남을지 나갈지 한쪽으로 기울고 싶을 때'],
      icon: 'compass',
      bgGradient: 'from-[#3a1a1a] to-[#2a1414]',
      borderColor: 'border-primary/40',
      iconColor: 'text-primary',
    },
    {
      id: 'one-3',
      topic: '직장',
      title: '이번 지원이나 시험, 진짜 합격권일까?',
      subtitle: '막연히 기대하기보다 지금 위치부터 짚어봅니다.',
      readingAngleLines: ['현재 경쟁력의 위치와', '결과를 가르는 약점을', '짧고 선명하게 확인합니다.'],
      sessionBeatLines: ['지금 위치를 냉정하게 보고', '남은 힘을 어디에 써야 할지 알고 싶을 때'],
      icon: 'heart',
      bgGradient: 'from-[#211813] to-[#15100d]',
      borderColor: 'border-[#8a6a4b]/45',
      iconColor: 'text-[#c79b66]',
    },
    {
      id: 'one-4',
      topic: '직장',
      title: '지금 상사는 나를 믿고 있을까?',
      subtitle: '눈치로 채운 빈칸 말고, 일 관계의 실제 온도를 봅니다.',
      readingAngleLines: ['상사가 보는 내 인상과', '신뢰를 깎는 오해를', '짧게 드러냅니다.'],
      sessionBeatLines: ['불필요한 추측을 걷어내고', '대응 방향을 빨리 잡고 싶을 때'],
      icon: 'eye',
      bgGradient: 'from-[#1a1c24] to-[#141416]',
      borderColor: 'border-[#8a6a4b]/45',
      iconColor: 'text-[#c79b66]',
    },
    {
      id: 'one-5',
      topic: '금전',
      title: '이번 달 돈, 어디서 새고 있을까?',
      subtitle: '무조건 아끼라는 말보다, 새는 구멍부터 봅니다.',
      readingAngleLines: ['자꾸 나가는 돈의 원인과', '바로 줄일 수 있는 지점을', '한 장으로 짚습니다.'],
      sessionBeatLines: ['새는 흐름을 확인하고', '이번 달 관리 포인트를 잡고 싶을 때'],
      icon: 'moon',
      bgGradient: 'from-[#24211a] to-[#1a1712]',
      borderColor: 'border-parchment/25',
      iconColor: 'text-parchment',
    },
  ],
  'three-card': [
    {
      id: 'three-1',
      topic: '연애',
      title: '끊긴 연락, 다시 이어질 수 있을까?',
      subtitle: '지금 마음, 막힌 이유, 다시 닿을 가능성을 세 장으로 봅니다.',
      readingAngleLines: ['상대의 현재 마음과', '연락이 막힌 핵심 이유,', '다시 이어질 가능성을 봅니다.'],
      sessionBeatLines: ['기대와 현실의 간격을 보고', '먼저 움직일지 기다릴지 감을 잡고 싶을 때'],
      icon: 'heart',
      bgGradient: 'from-[#3a1a1a] to-[#2a1414]',
      borderColor: 'border-primary/40',
      iconColor: 'text-primary',
    },
    {
      id: 'three-2',
      topic: '직장',
      title: '지금 이직하면, 지금보다 나은 자리로 갈 수 있을까?',
      subtitle: '떠나고 싶은 이유와 옮겨간 뒤의 결을 같이 봅니다.',
      readingAngleLines: ['현재 자리의 한계와', '이직을 성사시키는 변수,', '다음 자리의 결을 흐름으로 봅니다.'],
      sessionBeatLines: ['지금 옮기는 게 맞는지 보고', '어느 방향이 유리한지 읽고 싶을 때'],
      icon: 'compass',
      bgGradient: 'from-[#1a1c24] to-[#141416]',
      borderColor: 'border-[#8a6a4b]/45',
      iconColor: 'text-[#c79b66]',
    },
    {
      id: 'three-3',
      topic: '연애',
      title: '서먹해진 관계, 다시 편하게 돌아갈 수 있을까?',
      subtitle: '틀어진 감정과 풀릴 계기, 회복 속도를 흐름으로 봅니다.',
      readingAngleLines: ['지금 관계의 거리감과', '회복을 막는 감정의 매듭,', '다시 편해질 가능성을 봅니다.'],
      sessionBeatLines: ['누가 무엇에서 막혔는지 보고', '회복의 타이밍을 가늠하고 싶을 때'],
      icon: 'eye',
      bgGradient: 'from-[#24211a] to-[#1a1712]',
      borderColor: 'border-parchment/25',
      iconColor: 'text-parchment',
    },
    {
      id: 'three-4',
      topic: '금전',
      title: '내 수입은 석 달 안에 나아질 수 있을까?',
      subtitle: '막연한 기대 말고, 들어오는 흐름과 막힌 지점을 같이 봅니다.',
      readingAngleLines: ['현재 수입 흐름과', '늘어나지 못하는 이유,', '가까운 변화의 방향을 봅니다.'],
      sessionBeatLines: ['답답한 지점의 구조를 보고', '어디서 숨통이 트일지 확인하고 싶을 때'],
      icon: 'moon',
      bgGradient: 'from-[#2d1e18] to-[#1a1614]',
      borderColor: 'border-gold/30',
      iconColor: 'text-gold/80',
    },
    {
      id: 'three-5',
      topic: '직장',
      title: '상사와의 관계, 더 버티면 풀릴까 더 꼬일까?',
      subtitle: '일보다 사람이 더 힘든 시기라면, 그 공기의 방향을 봅니다.',
      readingAngleLines: ['지금 긴장의 원인과', '관계를 누그러뜨리는 변수,', '앞으로의 협업 흐름을 봅니다.'],
      sessionBeatLines: ['감정 소모의 핵심을 짚고', '버틸 만해질지 판단하고 싶을 때'],
      icon: 'coin',
      bgGradient: 'from-[#1d241a] to-[#151b13]',
      borderColor: 'border-[#9fd39b]/40',
      iconColor: 'text-[#9fd39b]',
    },
  ],
  'celtic-cross': [
    {
      id: 'celtic-1',
      topic: '직장',
      title: '지금 이직하면, 어떤 업종이나 역할에서 더 잘 풀릴까?',
      subtitle: '퇴사 여부를 넘어서, 다음 자리의 방향과 결을 봅니다.',
      readingAngleLines: ['지금 커리어에서 꺼내야 할 강점과', '피해야 할 역할,', '옮겼을 때 잘 풀리는 방향을 봅니다.'],
      sessionBeatLines: ['막연한 이직 욕구를 구체화하고', '다음 자리를 선명하게 잡고 싶을 때'],
      icon: 'sparkles',
      bgGradient: 'from-[#2d1e18] to-[#1a1614]',
      borderColor: 'border-gold/30',
      iconColor: 'text-gold/80',
    },
    {
      id: 'celtic-2',
      topic: '연애',
      title: '이 관계는 회복될까, 여기서 정리되는 쪽이 맞을까?',
      subtitle: '붙잡을 관계인지, 놓아야 덜 아픈 관계인지 끝까지 봅니다.',
      readingAngleLines: ['두 사람을 붙드는 진짜 감정과', '반복되는 충돌의 근원,', '회복과 정리 중 기우는 방향을 봅니다.'],
      sessionBeatLines: ['관계의 본심을 드러내고', '이어갈지 멈출지 판단하고 싶을 때'],
      icon: 'heart',
      bgGradient: 'from-[#3a1a1a] to-[#2a1414]',
      borderColor: 'border-primary/40',
      iconColor: 'text-primary',
    },
    {
      id: 'celtic-3',
      topic: '금전',
      title: '돈이 안 모이는 이유와 앞으로 수입이 열리는 곳은 어디일까?',
      subtitle: '지출 습관과 수입이 열릴 자리를 한 판 안에서 같이 봅니다.',
      readingAngleLines: ['돈이 새는 오래된 패턴과', '현실에서 막는 요인,', '이후 열리는 흐름을 같이 봅니다.'],
      sessionBeatLines: ['불안의 정체를 구조로 보고', '관리와 확장의 순서를 잡고 싶을 때'],
      icon: 'compass',
      bgGradient: 'from-[#1a1c24] to-[#141416]',
      borderColor: 'border-[#8a6a4b]/45',
      iconColor: 'text-[#c79b66]',
    },
    {
      id: 'celtic-4',
      topic: '직장',
      title: '이번 시험이나 면접, 합격을 가르는 진짜 변수는 무엇일까?',
      subtitle: '노력의 양보다, 결과를 뒤집을 변수가 무엇인지 봅니다.',
      readingAngleLines: ['현재 준비의 강점과 빈틈,', '합격을 막는 실제 장애물,', '결과를 바꾸는 마지막 보완점을 봅니다.'],
      sessionBeatLines: ['지금 전략의 허점을 드러내고', '합격 쪽으로 기울 실전 포인트를 보고 싶을 때'],
      icon: 'moon',
      bgGradient: 'from-[#24211a] to-[#1a1712]',
      borderColor: 'border-parchment/25',
      iconColor: 'text-parchment',
    },
    {
      id: 'celtic-5',
      topic: '직장',
      title: '상사와 회사 분위기까지 감안했을 때, 여기서 더 버틸 수 있을까?',
      subtitle: '사람, 일, 체력이 같이 무너지는 때라면 버팀의 한계선을 봅니다.',
      readingAngleLines: ['지금 소진을 키우는 핵심 원인과', '버틸 때 지키는 것, 잃는 것,', '떠날 때 열리는 현실 변화를 봅니다.'],
      sessionBeatLines: ['버티는 선택의 대가를 직면하고', '남을지 떠날지 긴 호흡으로 판단하고 싶을 때'],
      icon: 'eye',
      bgGradient: 'from-[#211813] to-[#15100d]',
      borderColor: 'border-[#8a6a4b]/45',
      iconColor: 'text-[#c79b66]',
    },
  ],
};

export default function ThemeSelectionPage() {
  const navigate = useNavigate();
  const { spread: spreadParam } = useParams<{ spread: SpreadType }>();
  const [hoveredQuestionId, setHoveredQuestionId] = useState<string | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<QuestionPreset | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [wallet, setWallet] = useState<WalletSummaryResponse | null>(null);
  const [isWalletLoading, setIsWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState('');

  const resolvedSpread = useMemo(() => {
    if (isSpreadType(spreadParam)) return spreadParam;
    const stored = sessionStorage.getItem('selected_spread');
    if (isSpreadType(stored)) return stored;
    return null;
  }, [spreadParam]);

  useEffect(() => {
    if (!resolvedSpread) {
      navigate('/spreads', { replace: true });
    }
  }, [navigate, resolvedSpread]);

  if (!resolvedSpread) {
    return null;
  }

  const questions = QUESTIONS_BY_SPREAD[resolvedSpread];
  const spreadCost = wallet?.spreadCosts?.[resolvedSpread] ?? getSpreadChuruCost(resolvedSpread);
  const spreadLabel = getSpreadLabel(resolvedSpread);

  useEffect(() => {
    let cancelled = false;

    const loadWalletContext = async () => {
      setIsWalletLoading(true);
      setWalletError('');

      try {
        const session = await getAuthSession();
        if (cancelled) return;

        const nextUser = session?.user ?? null;
        setUser(nextUser);

        if (nextUser) {
          const summary = await getWalletSummary();
          if (cancelled) return;
          setWallet(summary);
        } else {
          setWallet(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load wallet context:', error);
          setWalletError('츄르 보유량을 불러오지 못했습니다. 잠시 후 다시 확인해 주세요.');
        }
      } finally {
        if (!cancelled) {
          setIsWalletLoading(false);
        }
      }
    };

    void loadWalletContext();

    return () => {
      cancelled = true;
    };
  }, []);

  const continueToSelection = (question: QuestionPreset) => {
    const nextPersistKey = `stitch-selection:${resolvedSpread}:${question.topic}:${question.title}`;
    sessionStorage.removeItem(nextPersistKey);
    sessionStorage.removeItem('ny_pending_payment_reading');
    persistReadingSelection(resolvedSpread, question.topic, question.title);
    navigate(getSelectionRoute(resolvedSpread, question.topic));
  };

  const handleQuestionSelect = (question: QuestionPreset) => {
    trackEvent('theme_question_selected', {
      spread: resolvedSpread,
      topic: question.topic,
      question_id: question.id,
      churu_cost: spreadCost,
    });
    setSelectedQuestion(question);
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setIsConfirmOpen(false);
    setSelectedQuestion(null);
  };

  const handleLogin = () => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    trackEvent('login_started', { source: 'theme_selection', return_to: returnTo });
    startGoogleLogin(returnTo);
  };

  const handleMoveToRecharge = () => {
    if (!selectedQuestion) return;

    persistReadingSelection(resolvedSpread, selectedQuestion.topic, selectedQuestion.title);
    saveWalletRechargeIntent({
      type: 'selection',
      spread: resolvedSpread,
      topic: selectedQuestion.topic,
      question: selectedQuestion.title,
      requiredPoints: spreadCost,
    });
    navigate('/wallet/recharge');
  };

  const balancePoints = wallet?.balancePoints ?? 0;
  const freeReadings = wallet?.freeReadings ?? { 'one-card': 0, 'three-card': 0, 'celtic-cross': 0 };
  const freeReadingCount = freeReadings[resolvedSpread] ?? 0;
  const hasFreeReading = freeReadingCount > 0;
  const remainingPoints = Math.max(0, balancePoints - spreadCost);
  const hasEnoughPoints = balancePoints >= spreadCost;
  const hasEnoughAccess = hasEnoughPoints || hasFreeReading;

  return (
    <div className="relative flex min-h-screen flex-col overflow-x-hidden bg-background-dark font-display text-white selection:bg-primary selection:text-white">
      <div className="fixed inset-0 z-50 pointer-events-none bg-grain opacity-[0.07]" />

      <header className="absolute left-0 top-0 z-40 flex w-full items-center justify-start p-8 opacity-70 transition-opacity duration-300 hover:opacity-100">
        <Link
          to="/"
          aria-label="메인 페이지로 이동"
          className="flex items-center transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-gold/60 focus:ring-offset-2 focus:ring-offset-background-dark"
        >
          <span className="text-sm font-bold uppercase tracking-[0.3em] text-parchment">{SITE_CONFIG.serviceNameEn}</span>
        </Link>
        <button hidden
          type="button"
          onClick={() => navigate('/spreads')}
          className="hidden items-center gap-2 border border-[#8a6a4b]/60 bg-[#1f1612]/75 px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-[#e6d3bc] transition-colors hover:bg-[#2a1d17]"
        >
          <UiIcon name="arrow-left" className="h-4 w-4" />
          뒤로가기
        </button>
      </header>

      <main className="relative z-10 flex flex-grow flex-col items-center justify-center pb-16 pt-32">
        <div className="mb-16 px-6 text-center">
          <h1 className="mb-4 font-serif text-4xl font-extrabold leading-tight tracking-[-0.02em] text-[#f5efe7] md:text-5xl lg:text-6xl">
            질문을 선택하세요
          </h1>
          <FixedLines
            lines={THEME_INTRO_LINES[resolvedSpread]}
            as="p"
            className="mx-auto mt-4 max-w-2xl text-sm font-light tracking-wide text-parchment/70 md:text-base"
            lineClassName="md:whitespace-nowrap"
          />
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#b98532]/40 bg-[#19120e]/85 px-4 py-2 text-sm text-[#f4d595]">
            <UiIcon name="coin" className="h-4 w-4" />
            <span>{spreadLabel} 한 번에 {spreadCost}츄르가 필요합니다</span>
          </div>
        </div>

        <div className="mb-10 w-full max-w-6xl px-6">
          <div className="grid gap-3 border border-[#6e5238]/50 bg-[#17120f]/80 p-4 shadow-[0_18px_50px_-30px_rgba(0,0,0,0.85)] md:grid-cols-3">
            {SESSION_GUIDE_CARDS.map((card) => (
              <div key={card.eyebrow} className="border border-[#5f4630]/50 bg-[#120f0d]/85 px-4 py-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#fbbf24]/50">{card.eyebrow}</p>
                <FixedLines lines={card.lines} className="mt-2 space-y-1 text-sm leading-6 text-[#f6ead8]" />
              </div>
            ))}
          </div>
        </div>

        <div className="mx-auto w-full max-w-6xl px-6">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 md:gap-10">
            {questions.map((question) => {
              const isHovered = hoveredQuestionId === question.id;

              return (
                <div
                  key={question.id}
                  className="perspective-1000"
                  onMouseEnter={() => setHoveredQuestionId(question.id)}
                  onMouseLeave={() => setHoveredQuestionId(null)}
                >
                  <button
                    type="button"
                    onClick={() => handleQuestionSelect(question)}
                    className={`relative w-full rounded-xl border ${question.borderColor} bg-gradient-to-br ${question.bgGradient} p-7 text-left transition-all duration-300 ${
                      isHovered ? '-translate-y-1 border-[#c79b66]/70' : ''
                    }`}
                  >
                    <div className={`mb-5 flex justify-center ${question.iconColor}`}>
                      <UiIcon name={question.icon} className="h-16 w-16" />
                    </div>

                    <h2 className="min-h-[4.8rem] text-center text-lg font-bold leading-snug text-white md:min-h-[4.2rem] md:text-xl">
                      {question.title}
                    </h2>

                    <p className={`mb-5 mt-3 text-center text-xs font-bold uppercase tracking-[0.16em] ${question.iconColor}`}>
                      {question.subtitle}
                    </p>

                    <div className={`mx-auto mb-5 h-[1px] w-16 bg-gradient-to-r from-transparent via-current to-transparent ${question.iconColor}`} />

                    <div className="mb-5 border border-[#5f4630]/50 bg-black/20 px-4 py-3">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-parchment/45">묘안이 읽는 방식</p>
                      <FixedLines lines={question.readingAngleLines} as="p" className="mt-2 text-sm leading-6 text-parchment/85" />
                    </div>

                    <div className="mb-5 border border-[#5f4630]/50 bg-black/15 px-4 py-3 text-left">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-parchment/45">이 질문이 잘 맞는 순간</p>
                      <FixedLines lines={question.sessionBeatLines} as="p" className="mt-2 text-xs leading-6 text-parchment/72" />
                    </div>

                    <div className="flex items-center justify-between text-[11px]">
                      <div>
                        <span className="uppercase tracking-widest text-parchment/45">주제</span>
                        <p className={`mt-2 text-sm font-bold tracking-wide ${question.iconColor}`}>{question.topic}</p>
                      </div>
                      <div className="rounded-full border border-[#b98532]/45 bg-[#21150f]/90 px-3 py-2 text-sm font-semibold text-[#f7d48a]">
                        {spreadCost}츄르
                      </div>
                    </div>

                    {isHovered && (
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#c79b66]">
                          <UiIcon name="arrow-right" className="h-4 w-4" />
                          <span>이 질문으로 읽기</span>
                        </div>
                      </div>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </main>
      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
      `}</style>

      {isConfirmOpen && selectedQuestion && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center px-4 py-10">
          <button
            type="button"
            aria-label="닫기"
            className="absolute inset-0 bg-black/72 backdrop-blur-md"
            onClick={handleCloseConfirm}
          />
          <div className="relative z-[161] w-full max-w-xl rounded-[30px] border border-[#a06d1b]/50 bg-[linear-gradient(180deg,rgba(24,17,13,0.98)_0%,rgba(16,11,9,0.98)_100%)] p-6 shadow-[0_35px_90px_-40px_rgba(0,0,0,0.95)] sm:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#c79b66]">{spreadLabel} 시작 전 확인</p>
                <h2 className="mt-3 text-2xl font-semibold leading-snug text-[#f8e3be]">{selectedQuestion.title}</h2>
              </div>
              <button
                type="button"
                onClick={handleCloseConfirm}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#7a5938]/50 bg-[#1b1310]/90 text-[#d3a557] transition-colors hover:border-[#b8842f]"
              >
                <UiIcon name="close" className="h-4 w-4" />
              </button>
            </div>

            <p className="mt-4 text-sm leading-7 text-[#dfc3a0]/84">
              이 질문으로 갈게요. 카드를 모두 고르면 {spreadCost}츄르가 차감되고, 바로 리딩이 열립니다.
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="rounded-[20px] border border-[#6d4d32]/45 bg-[#17100d]/86 px-4 py-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#bf9461]">현재 보유량</p>
                <p className="mt-3 text-2xl font-semibold text-[#f7e7c9]">
                  {isWalletLoading ? '...' : `${balancePoints}츄르`}
                </p>
              </div>
              <div className="rounded-[20px] border border-[#6d4d32]/45 bg-[#17100d]/86 px-4 py-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#bf9461]">이번 소모량</p>
                <p className="mt-3 text-2xl font-semibold text-[#f7d48a]">{spreadCost}츄르</p>
              </div>
              <div className="rounded-[20px] border border-[#6d4d32]/45 bg-[#17100d]/86 px-4 py-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#bf9461]">소모 후 잔액</p>
                <p className={`mt-3 text-2xl font-semibold ${hasEnoughPoints ? 'text-[#dcefb8]' : 'text-[#ffbea8]'}`}>
                  {isWalletLoading ? '...' : `${remainingPoints}츄르`}
                </p>
              </div>
            </div>

            <div className="mt-6 rounded-[22px] border border-[#6f5034]/45 bg-[#17110d]/86 px-5 py-4">
              {!user ? (
                <>
                  <p className="text-sm leading-7 text-[#efd9bf]">
                    츄르 잔액을 보려면 먼저 로그인해야 합니다. 보관함과 지갑도 같은 계정에 묶입니다.
                  </p>
                  <button
                    type="button"
                    onClick={handleLogin}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#b8842f]/50 bg-[#241811] px-5 py-3 text-sm font-semibold text-[#f7d48a] transition-colors hover:bg-[#2f1f16]"
                  >
                    <UiIcon name="arrow-right" className="h-4 w-4" />
                    로그인하고 계속하기
                  </button>
                </>
              ) : walletError ? (
                <div className="space-y-4">
                  <p className="text-sm leading-7 text-[#ffd0c4]">{walletError}</p>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#8b5d42] px-5 py-3 text-sm text-[#f4debf] transition-colors hover:bg-[#241913]"
                  >
                    다시 불러오기
                  </button>
                </div>
              ) : isWalletLoading ? (
                <p className="text-sm leading-7 text-[#efd9bf]">츄르 보유량을 확인하고 있습니다.</p>
              ) : hasEnoughAccess ? (
                <>
                  <p className="text-sm leading-7 text-[#efd9bf]">
                    지금 시작하면 {spreadCost}츄르가 차감됩니다. 카드만 고르면 바로 리딩으로 이어집니다.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      trackEvent('card_selection_started', {
                        spread: resolvedSpread,
                        topic: selectedQuestion.topic,
                        churu_cost: spreadCost,
                      });
                      continueToSelection(selectedQuestion);
                    }}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#b8842f]/50 bg-[#2a1c13] px-5 py-3 text-sm font-semibold text-[#f7d48a] transition-colors hover:bg-[#322117]"
                  >
                    <UiIcon name="sparkles" className="h-4 w-4" />
                    카드 고르러 가기
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm leading-7 text-[#efd9bf]">
                    지금은 {balancePoints}츄르가 있어서 이번 세션을 바로 열기엔 부족합니다. 먼저 충전하고 이어갈게요.
                  </p>
                  <button
                    type="button"
                    onClick={handleMoveToRecharge}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#b8842f]/50 bg-[#2a1c13] px-5 py-3 text-sm font-semibold text-[#f7d48a] transition-colors hover:bg-[#322117]"
                  >
                    <UiIcon name="coin" className="h-4 w-4" />
                    츄르 충전하기
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
